import React, { useState } from 'react';
import {
  Button,
  Checkbox,
  Divider,
  FormControlLabel,
  Grid,
  InputAdornment,
  TextField,
  Typography,
} from '@mui/material';
import { useTranslation } from 'react-i18next';
import { useFormikContext } from 'formik';
import { AccountData } from '../../../../../api';
import { useAuth } from '../../../../../session/InternalAuthProvider';
import { useLanguage } from '../../../../../common/GeneralUtils';
import { bookingFeeAmount, formatMoney, isBookingFeeOn, withBookingFee } from '../../../../../common/bookingFee';
import { customerFacingPriceFormatter, getCurrencySymbol } from '../../../../../utils/utils';
import { EditOrderFormValues } from '../../EditOrderTypes';
// @ts-ignore
import { useEditOrder } from '../../EditOrderContext';
import { PaymentLinkHoldFields } from '../../components/paymentLinksPanel/PaymentLinksFields';
import { PaymentModel, perPersonDepositOf } from './paymentModel';
import { usePaymentModel } from './usePaymentModel';

const AMOUNT_FIELD_WIDTH = '120px';

type ReceiptRowProps = {
  label: string;
  isBold?: boolean;
  isMuted?: boolean;
  children: React.ReactNode;
};

const ReceiptRow = ({ label, isBold, isMuted, children }: ReceiptRowProps) => (
  <Grid
    container
    alignItems="center"
    justifyContent="space-between"
    flexWrap="nowrap"
    gap={1.5}
    sx={{ minHeight: 44, px: '14px', py: '4px' }}>
    <Typography sx={{ fontWeight: isBold ? 700 : 400 }} color={isMuted ? 'text.secondary' : undefined}>
      {label}
    </Typography>
    <Grid container item width="auto" alignItems="center" justifyContent="flex-end" flexWrap="nowrap" gap={1}>
      {children}
    </Grid>
  </Grid>
);

const ReceiptHint = ({ text }: { text: string }) => (
  <Typography variant="caption" color="text.secondary" sx={{ px: '14px', pb: '8px', mt: '-4px' }}>
    {text}
  </Typography>
);

type AmountProps = {
  amount: string;
  isBold?: boolean;
  isMuted?: boolean;
};

const Amount = ({ amount, isBold, isMuted }: AmountProps) => (
  <Typography
    dir="ltr"
    color={isMuted ? 'text.secondary' : undefined}
    sx={{ fontVariantNumeric: 'tabular-nums', fontWeight: isBold ? 700 : 400 }}>
    {amount}
  </Typography>
);

type AmountFieldProps = {
  value: number;
  hasError?: boolean;
  onChange: (value: number) => void;
  onBlur?: () => void;
  autoFocus?: boolean;
};

const AmountField = ({ value, hasError, onChange, onBlur, autoFocus }: AmountFieldProps) => {
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  return (
    <TextField
      dir="ltr"
      type="number"
      size="small"
      value={value}
      error={hasError}
      autoFocus={autoFocus}
      onChange={(e) => onChange(+e.target.value || 0)}
      onBlur={onBlur}
      InputProps={{
        startAdornment: <InputAdornment position="start">{getCurrencySymbol(account)}</InputAdornment>,
        inputProps: { min: 0, inputMode: 'decimal' },
      }}
      sx={{
        width: AMOUNT_FIELD_WIDTH,
        '& input': { fontVariantNumeric: 'tabular-nums', MozAppearance: 'textfield' },
        '& input::-webkit-outer-spin-button, & input::-webkit-inner-spin-button': { WebkitAppearance: 'none', m: 0 },
      }}
    />
  );
};

type EditableAmountProps = {
  value: number;
  amount: string;
  onChange: (value: number) => void;
};

// Shows the amount with an Edit button; tapping it turns the amount into an inline field.
const EditableAmount = ({ value, amount, onChange }: EditableAmountProps) => {
  const { t } = useTranslation();
  const [isEditing, setIsEditing] = useState(false);
  if (isEditing) {
    return <AmountField autoFocus value={value} onChange={onChange} onBlur={() => setIsEditing(false)} />;
  }
  return (
    <>
      <Amount amount={amount} />
      <Button size="small" variant="text" onClick={() => setIsEditing(true)} sx={{ textTransform: 'none' }}>
        {t('calendar.create_order.payment.edit')}
      </Button>
    </>
  );
};

type PaymentReceiptProps = {
  // False when the order cannot be paid by a link (e.g. editing): the receipt then has no link checkbox.
  showLinkCheckbox: boolean;
  // Set after a failed Create so the missing link amount is flagged before the field is touched.
  showLinkAmountError: boolean;
};

export const PaymentReceipt = ({ showLinkCheckbox, showLinkAmountError }: PaymentReceiptProps) => {
  const { t } = useTranslation();
  const language = useLanguage();
  const { authState } = useAuth();
  const account = authState.account as unknown as AccountData;
  const { values, setFieldValue } = useFormikContext<EditOrderFormValues>();
  // @ts-ignore
  const { editOrderState, setPrice, setCreatePaymentLink } = useEditOrder();
  const { model, deposit } = usePaymentModel(false);
  const [isUpfrontTouched, setIsUpfrontTouched] = useState(false);

  const orderPrice: number = editOrderState.details.price;
  const { persons } = editOrderState.details;
  const isLinkOn = showLinkCheckbox && values.createPaymentLink;
  const activity = account.activity_label_singular?.[language] ?? t('passes.activity_fallback_singular');
  const format = (amount: number) => customerFacingPriceFormatter(amount, account) as string;
  const isLinkAmountChecked = isUpfrontTouched || showLinkAmountError;
  const hasLinkAmountError = isLinkOn && values.upfrontAmount <= 0 && isLinkAmountChecked;
  const hasLinkAmountAboveError =
    isLinkOn && model !== PaymentModel.Deposit && values.upfrontAmount > orderPrice && isLinkAmountChecked;
  const fixAmount = model === PaymentModel.Deposit ? deposit ?? 0 : orderPrice;

  const setUpfrontAmount = (amount: number) => {
    void setFieldValue('upfrontAmount', amount);
    void setFieldValue('upfrontEdited', true);
  };

  const fixLinkAmount = () => {
    void setFieldValue('upfrontAmount', fixAmount);
    void setFieldValue('upfrontEdited', false);
  };

  const perPersonDeposit = perPersonDepositOf(editOrderState.orderPage, account);
  // A percentage deposit has no per-person amount, so its final price is only known at the visit.
  const depositHintText =
    perPersonDeposit && perPersonDeposit > 0
      ? t('calendar.create_order.payment.depositHint', { amount: format(perPersonDeposit), persons })
      : t('calendar.create_order.payment.finalPriceAtVisit');

  const isBookingFeeShown = isLinkOn && isBookingFeeOn(account) && values.upfrontAmount > 0;

  const linkAmountRow = (label: string) => (
    <>
      <ReceiptRow label={label}>
        <AmountField
          value={values.upfrontAmount}
          hasError={hasLinkAmountError}
          onChange={setUpfrontAmount}
          onBlur={() => setIsUpfrontTouched(true)}
        />
      </ReceiptRow>
      {isBookingFeeShown && (
        <>
          <ReceiptRow label={t('calendar.create_order.payment.bookingFee')} isMuted>
            <Amount amount={formatMoney(bookingFeeAmount(values.upfrontAmount, account), account)} isMuted />
          </ReceiptRow>
          <ReceiptRow label={t('calendar.create_order.payment.customerPaysByLink')}>
            <Amount amount={formatMoney(withBookingFee(values.upfrontAmount, account), account)} />
          </ReceiptRow>
        </>
      )}
    </>
  );

  const renderLines = () => {
    if (model === PaymentModel.Deposit) {
      return (
        <>
          {isLinkOn ? (
            linkAmountRow(t('calendar.create_order.payment.depositByLink'))
          ) : (
            <ReceiptRow label={t('calendar.create_order.payment.deposit')}>
              <EditableAmount
                value={values.upfrontAmount}
                amount={format(values.upfrontAmount)}
                onChange={setUpfrontAmount}
              />
            </ReceiptRow>
          )}
          <ReceiptHint text={depositHintText} />
          <Divider />
          <ReceiptRow label={t('calendar.create_order.payment.atVisit')} isBold>
            <Typography sx={{ fontWeight: 700, textAlign: 'end' }}>
              {t('calendar.create_order.payment.atVisitMinusDeposit', { deposit: format(values.upfrontAmount) })}
            </Typography>
          </ReceiptRow>
        </>
      );
    }
    if (model === PaymentModel.PayAtVisit) {
      return (
        <>
          <ReceiptRow label={t('calendar.create_order.payment.atVisit')} isBold>
            <Typography sx={{ fontWeight: 700, textAlign: 'end' }}>
              {t('calendar.create_order.payment.atVisitByChoice')}
            </Typography>
          </ReceiptRow>
          <ReceiptHint text={t('calendar.create_order.payment.noUpfront')} />
          {isLinkOn && linkAmountRow(t('calendar.create_order.payment.linkAmount'))}
        </>
      );
    }
    return (
      <>
        <ReceiptRow label={t('calendar.create_order.payment.orderTotal')}>
          {editOrderState.shouldAllowPriceOverride ? (
            <EditableAmount value={orderPrice} amount={format(orderPrice)} onChange={setPrice} />
          ) : (
            <Amount amount={format(orderPrice)} />
          )}
        </ReceiptRow>
        <ReceiptHint
          text={t('calendar.create_order.payment.experiencePrice', {
            price: format(editOrderState.calculatedPrice),
            activity,
          })}
        />
        {isLinkOn && linkAmountRow(t('calendar.create_order.payment.linkAmount'))}
        {isLinkOn && <ReceiptHint text={t('calendar.create_order.payment.partialHint')} />}
        <Divider />
        <ReceiptRow
          label={t(
            isLinkOn ? 'calendar.create_order.payment.balanceAfterLink' : 'calendar.create_order.payment.balanceDue',
          )}
          isBold>
          <Amount amount={format(isLinkOn ? Math.max(0, orderPrice - values.upfrontAmount) : orderPrice)} isBold />
        </ReceiptRow>
      </>
    );
  };

  return (
    <Grid container direction="column" gap={1}>
      <Typography variant="subtitle2">{t('calendar.create_order.payment.title')}</Typography>
      <Grid container direction="column" flexWrap="nowrap" sx={{ bgcolor: 'mb.ground', borderRadius: '6px' }}>
        {renderLines()}
      </Grid>
      {(hasLinkAmountError || hasLinkAmountAboveError) && (
        <Grid container alignItems="center" gap={1}>
          <Typography variant="caption" color="error">
            {hasLinkAmountError
              ? t('calendar.create_order.payment.linkAmountRequired', { zero: format(0) })
              : t('calendar.create_order.payment.linkAmountAbovePrice', { amount: format(orderPrice) })}
          </Typography>
          {fixAmount > 0 && (
            <Button size="small" variant="text" onClick={fixLinkAmount} sx={{ textTransform: 'none' }}>
              {model === PaymentModel.Deposit
                ? t('calendar.create_order.payment.useDeposit', { amount: format(deposit ?? 0), activity })
                : t('calendar.create_order.payment.useFullPrice', { amount: format(orderPrice) })}
            </Button>
          )}
        </Grid>
      )}
      {showLinkCheckbox && (
        <FormControlLabel
          control={<Checkbox />}
          checked={values.createPaymentLink}
          label={t(
            model === PaymentModel.Deposit
              ? 'calendar.create_order.payment.sendDepositLink'
              : 'calendar.create_order.payment.sendLink',
          )}
          onChange={(e: any) => {
            void setFieldValue('createPaymentLink', e.target.checked);
            if (model === PaymentModel.PayAtVisit) {
              void setFieldValue('upfrontEdited', false);
            }
            setCreatePaymentLink(e.target.checked);
          }}
        />
      )}
      {isLinkOn && <PaymentLinkHoldFields />}
    </Grid>
  );
};
